import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Search, Globe, Star, CalendarDays, Bookmark, TrendingUp, User } from 'lucide-react';
import { addDays, parseISO } from 'date-fns';

const UPCOMING_LOAD_WINDOW_DAYS = 28; // next 4 weeks

function StarRating({ value }) {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="flex items-center gap-0.5">
      {stars.map(i => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(value || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'}`} />
      ))}
      {value && <span className="text-xs text-muted-foreground ml-1">{value.toFixed(1)}</span>}
    </div>
  );
}

const sessionTrainerRefs = (s) => {
  if (s.session_trainers && s.session_trainers.length > 0) {
    return s.session_trainers.filter(t => t.name).map(t => ({ id: t.trainer_id || '', name: t.name }));
  }
  const refs = [];
  if (s.trainer_name) refs.push({ id: s.trainer_id || '', name: s.trainer_name });
  if (s.trainer_2_name) refs.push({ id: '', name: s.trainer_2_name });
  return refs;
};

const sessionMatchesTrainer = (s, trainer) =>
  sessionTrainerRefs(s).some(ref => (ref.id && ref.id === trainer.id) || (!ref.id && ref.name === trainer.name));

const loadBadge = (count) => {
  if (count >= 3) return { label: 'High Load', className: 'bg-red-100 text-red-800' };
  if (count >= 1) return { label: 'Normal', className: 'bg-green-100 text-green-800' };
  return { label: 'Available', className: 'bg-gray-100 text-gray-600' };
};

export const PrivateDeliveryTrainersTab = ({ sessions = [], trainers = [], loading }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [highlightFilter, setHighlightFilter] = useState('all');

  const cutoff = useMemo(() => addDays(new Date(), UPCOMING_LOAD_WINDOW_DAYS), []);

  const scopedTrainers = useMemo(() => {
    return trainers
      .map(t => {
        const matched = sessions.filter(s => sessionMatchesTrainer(s, t));
        const upcoming = matched.filter(s => {
          if (!s.session_date) return false;
          try { return parseISO(s.session_date) >= new Date() && parseISO(s.session_date) <= cutoff; }
          catch { return false; }
        });
        return { ...t, pd_session_count: matched.length, pd_upcoming_count: upcoming.length };
      })
      .filter(t => t.pd_session_count > 0);
  }, [trainers, sessions, cutoff]);

  const filtered = scopedTrainers
    .filter(t => {
      const s = search.toLowerCase();
      const matchSearch = !search || t.name?.toLowerCase().includes(s) || t.email?.toLowerCase().includes(s) || (t.expertise_tags || []).some(tag => tag.toLowerCase().includes(s));
      const matchCountry = countryFilter === 'all' || t.country === countryFilter;
      const matchHighlight =
        highlightFilter === 'all' ? true :
        highlightFilter === 'featured' ? !!t.is_featured :
        highlightFilter === 'top_rated' ? t.avg_rating != null :
        highlightFilter === 'most_utilized' ? t.pd_session_count > 0 : true;
      return matchSearch && matchCountry && matchHighlight;
    })
    .sort((a, b) => {
      if (highlightFilter === 'top_rated') return (b.avg_rating || 0) - (a.avg_rating || 0);
      if (highlightFilter === 'most_utilized') return b.pd_session_count - a.pd_session_count;
      return 0;
    });

  const upcomingLoadRows = [...scopedTrainers].sort((a, b) => b.pd_upcoming_count - a.pd_upcoming_count);
  const uniqueCountries = [...new Set(scopedTrainers.map(t => t.country).filter(Boolean))].sort();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => <div key={i} className="h-48 bg-muted animate-pulse rounded-xl" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pd-trainers-tab">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search by name, email, expertise..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="pd-search-trainers" />
        </div>
        {uniqueCountries.length > 0 && (
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger className="w-44"><SelectValue placeholder="All Countries" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Countries</SelectItem>
              {uniqueCountries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="flex flex-wrap gap-2" data-testid="pd-highlight-filters">
        {[
          { key: 'all', label: 'All Trainers' },
          { key: 'featured', label: 'Featured' },
          { key: 'top_rated', label: 'Top Rated' },
          { key: 'most_utilized', label: 'Most Utilized' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setHighlightFilter(f.key)}
            data-testid={`pd-highlight-filter-${f.key}`}
            className={`text-sm px-4 py-1.5 rounded-full border transition-colors font-medium ${
              highlightFilter === f.key
                ? 'bg-lime text-lime-foreground border-lime'
                : 'border-border bg-background hover:border-lime/50 text-muted-foreground'
            }`}
          >
            {f.key === 'featured' && <Bookmark className="w-3.5 h-3.5 inline mr-1.5" />}
            {f.key === 'top_rated' && <Star className="w-3.5 h-3.5 inline mr-1.5" />}
            {f.key === 'most_utilized' && <TrendingUp className="w-3.5 h-3.5 inline mr-1.5" />}
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(trainer => {
            const badge = loadBadge(trainer.pd_upcoming_count);
            return (
              <Card key={trainer.id} className="hover:shadow-md transition-shadow" data-testid={`pd-trainer-card-${trainer.id}`}>
                <CardContent className="pt-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="relative cursor-pointer" onClick={() => navigate(`/trainers/${trainer.id}`)}>
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {trainer.name.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold leading-tight hover:text-primary cursor-pointer" onClick={() => navigate(`/trainers/${trainer.id}`)}>
                        {trainer.name}
                      </h3>
                      {trainer.country && <p className="text-xs text-muted-foreground flex items-center gap-1"><Globe className="w-3 h-3" />{trainer.country}</p>}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <button
                      onClick={() => navigate(`/sessions?trainer=${encodeURIComponent(trainer.name)}`)}
                      className="flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors group"
                      data-testid={`pd-trainer-sessions-link-${trainer.id}`}
                    >
                      <CalendarDays className="w-3.5 h-3.5" />
                      <span className="group-hover:underline">{trainer.pd_session_count} PD sessions</span>
                    </button>
                    {trainer.avg_rating ? <StarRating value={trainer.avg_rating} /> : <span className="text-xs text-muted-foreground">No ratings</span>}
                  </div>
                  <div className="mt-2">
                    <Badge className={`text-xs ${badge.className}`}>{badge.label} · {trainer.pd_upcoming_count} upcoming (4wk)</Badge>
                  </div>
                  {(trainer.expertise_tags || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {trainer.expertise_tags.slice(0, 4).map(tag => <Badge key={tag} className="text-xs bg-blue-100 text-blue-800 font-normal">{tag}</Badge>)}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No trainers found for Private Deliveries</h3>
          <p className="text-muted-foreground">{search ? 'Try adjusting your search.' : 'Trainers assigned to Private Delivery sessions will appear here.'}</p>
        </div>
      )}

      <Card data-testid="pd-trainer-load-card">
        <CardHeader>
          <CardTitle className="font-heading text-base">Trainer Utilization & Load Balance</CardTitle>
          <CardDescription className="text-xs">Upcoming Private Deliveries load, next 4 weeks — spot over- and under-booked trainers</CardDescription>
        </CardHeader>
        <CardContent>
          {upcomingLoadRows.length > 0 ? (
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Trainer</TableHead>
                    <TableHead>Total PD Sessions</TableHead>
                    <TableHead>Upcoming (4wk)</TableHead>
                    <TableHead>Load</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {upcomingLoadRows.map(t => {
                    const badge = loadBadge(t.pd_upcoming_count);
                    return (
                      <TableRow key={t.id} data-testid={`pd-load-row-${t.id}`}>
                        <TableCell className="text-sm font-medium">{t.name}</TableCell>
                        <TableCell className="text-sm">{t.pd_session_count}</TableCell>
                        <TableCell className="text-sm">{t.pd_upcoming_count}</TableCell>
                        <TableCell><Badge className={`text-xs ${badge.className}`}>{badge.label}</Badge></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4 text-sm">No trainer data yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PrivateDeliveryTrainersTab;
